import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { FormCombobox } from "src/core/components/custom-ui/form-combobox";
import { FormInput } from "src/core/components/custom-ui/form-input";
import { FormSelect } from "src/core/components/custom-ui/form-select";
import { SpinnerFullPage } from "src/core/components/custom-ui/spinner-full-page";
import { Button } from "src/core/components/ui/button";
import { ISelectOption } from "src/core/constants/options";
import { useCountryOptions } from "src/core/hooks/common/useCountryOptions";
import { useFolderEntrepriseForm } from "src/core/hooks/folder/useFolderEntrepriseForm";
import { useRequiredTenantPaths } from "src/core/hooks/routing/useTenantPaths";
import { cn } from "src/core/lib/utils";
import { withRequiredOverride } from "src/core/overrides";

const DocumentUploadFieldsetOverride = withRequiredOverride(
  "components.folder.DocumentsUploadFieldset"
);

type FolderEntrepriseProps = {
  inModal?: boolean;
  setOpen?: (open: boolean) => void;
  onSuccess?: () => void;
};

export const FolderEntreprise: React.FC<FolderEntrepriseProps> = ({
  inModal,
  setOpen,
  onSuccess,
}) => {
  const { t } = useTranslation();

  const paths = useRequiredTenantPaths();

  const formData = useFolderEntrepriseForm(onSuccess ?? undefined);
  const {
    countryOptions,
    loading: countryLoading,
  }: { countryOptions: ISelectOption[]; loading: boolean } = useCountryOptions();

  if (!formData) {
    return <SpinnerFullPage />;
  }

  const {
    requiredDocuments,
    form,
    countryWithRiskOptions,
    activityWithRiskOptions,
    legalTypesWithRiskOptions,
    legalStatusOptions,
    legalStatusOptionsLoading,
    showFormeJuridiquePrecision,
    onSubmit,
    isLoading,
    duplicateFolder,
    missingInfo,
    missingDocuments,
    isReadOnly,
    incompleteFields,
  } = formData;

  const folderId = duplicateFolder?.id;
  const folderUrl = folderId ? paths.folder(folderId) : null;

  return (
    <div className="folder-entreprise-wrapper">
      {!inModal && (
        <div className="folder-tab-banner">
          <div className="section-title">{t("folder.entreprise.title")}</div>
        </div>
      )}
      <div className={!inModal ? "folder-entreprise-data" : ""}>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="section-title">
              {t("folder.documents.title")}
              {missingDocuments > 0 && (
                <span className="item-counter-alert">{missingDocuments}</span>
              )}
            </div>
            <DocumentUploadFieldsetOverride
              requiredDocuments={requiredDocuments}
              disabled={isReadOnly}
              incompleteFields={incompleteFields}
            />
            <div className="section-title">
              {t("folder.fields.title")}
              {missingInfo > 0 && <span className="item-counter-alert">{missingInfo}</span>}
            </div>
            <div className="folder-entreprise-fields">
              <FormInput
                control={form.control}
                name={"raisonSociale"}
                required
                softRequired={incompleteFields.has("raisonSociale")}
                label={t("form.entreprise.raisonSociale")}
                className="w-full"
                disabled={isReadOnly}
              />
              <FormCombobox
                control={form.control}
                name={"activite"}
                required
                softRequired={incompleteFields.has("activite")}
                label={t("form.entreprise.activite")}
                options={activityWithRiskOptions}
                className="w-full"
                withBadge
                disabled={isReadOnly}
                isInModal={inModal}
              />
              <FormCombobox
                control={form.control}
                name={"paysImmatriculation"}
                required
                softRequired={incompleteFields.has("paysImmatriculation")}
                label={t("form.entreprise.paysImmatriculation")}
                options={countryWithRiskOptions}
                className="w-full"
                withBadge
                disabled={isReadOnly}
                isInModal={inModal}
              />
              <div className="grid grid-cols-2 gap-x-10 gap-y-20">
                <div className="flex flex-col gap-y-10">
                  <FormInput
                    control={form.control}
                    name={"siren"}
                    required
                    softRequired={incompleteFields.has("siren")}
                    label={t("form.entreprise.siren")}
                    className="w-full"
                    disabled={isReadOnly}
                  />
                  {!isReadOnly &&
                    form.formState.errors?.siren?.type === "duplicate" &&
                    folderUrl && (
                      <Link
                        to={folderUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="action-link"
                      >
                        {t("form.createClient.seeFolder")}
                      </Link>
                    )}
                </div>
                <FormSelect
                  control={form.control}
                  name={"typeJuridique"}
                  required
                  softRequired={incompleteFields.has("typeJuridique")}
                  label={t("form.entreprise.typeJuridique")}
                  options={legalTypesWithRiskOptions}
                  className="w-full"
                  withBadge
                  disabled={isReadOnly}
                />
              </div>
              <FormSelect
                control={form.control}
                name={"formeJuridique"}
                required
                softRequired={incompleteFields.has("formeJuridique")}
                label={t("form.entreprise.formeJuridique")}
                options={legalStatusOptions}
                isLoading={legalStatusOptionsLoading}
                className="w-full"
                disabled={isReadOnly}
              />
              {showFormeJuridiquePrecision ? (
                <FormInput
                  control={form.control}
                  name={"formeJuridiquePrecision"}
                  required
                  softRequired={incompleteFields.has("formeJuridiquePrecision")}
                  label={t("form.entreprise.formeJuridiquePrecision")}
                  className="w-full"
                  disabled={isReadOnly}
                />
              ) : (
                <div className="h-[70px] w-full" aria-hidden />
              )}

              <FormInput
                control={form.control}
                name={"adresse"}
                required
                softRequired={incompleteFields.has("adresse")}
                label={t("form.entreprise.adresse")}
                className="w-full"
                disabled={isReadOnly}
              />
              <div className="grid grid-cols-2 gap-x-10 gap-y-20">
                <div className="flex flex-col gap-y-10">
                  <FormInput
                    control={form.control}
                    name={"codePostal"}
                    required
                    softRequired={incompleteFields.has("codePostal")}
                    label={t("form.entreprise.codePostal")}
                    className="w-full"
                    disabled={isReadOnly}
                  />
                </div>
                <FormCombobox
                  control={form.control}
                  name="pays"
                  required
                  softRequired={incompleteFields.has("pays")}
                  label={t("form.entreprise.pays")}
                  options={countryOptions}
                  isLoading={countryLoading}
                  className="w-full"
                  disabled={isReadOnly}
                />
              </div>
            </div>

            <div
              className={cn(
                "flex flex-row",
                inModal &&
                  "justify-center gap-x-40 border-t border-border pt-10 sticky bottom-0 bg-gray-xlight",
                !inModal && "justify-end"
              )}
            >
              {inModal && (
                <Button
                  variant="link"
                  onClick={(e) => {
                    e.preventDefault();
                    setOpen?.(false);
                  }}
                >
                  {t(isReadOnly ? "global.form.close" : "global.form.cancel")}
                </Button>
              )}
              {!isReadOnly && (
                <Button type="submit" loading={isLoading}>
                  {t("form.entreprise.submit")}
                </Button>
              )}
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};
